import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/contexts/StoreContext';
import { generateSeed } from '@/utils/handDrawn';
import type { TextElement } from '@/types';

interface TextProps {
  element: TextElement;
  isSelected: boolean;
}

export const Text: React.FC<TextProps> = observer(({ element, isSelected }) => {
  const { uiStore, projectStore } = useStores();
  const { x, y, text, style, width, height, id } = element;
  const { fontSize, textAlign, textType } = style;
  const isEditing = uiStore.editingTextId === element.id;
  
  const seed = generateSeed(id);
  const rotation = (seed % 5 - 2) * 0.5;
  const handDrawnFontFamily = 'Caveat, Kalam, "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive';
  const monospaceFontFamily = '"JetBrains Mono", "Fira Code", "Consolas", "Monaco", "Courier New", monospace';
  
  const fontFamily = textType === 'pre' ? monospaceFontFamily : handDrawnFontFamily;
  
  const [editValue, setEditValue] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Calculate line height for proper text rendering
  const lineHeight = fontSize * 1.2;
  const textLines = text.split('\n');

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
      // Auto-resize textarea to fit content
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);



  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    const newValue = e.target.value;
    setEditValue(newValue);
    
    // Calculate new dimensions based on content
    const lines = newValue.split('\n');
    const maxLineLength = Math.max(...lines.map(line => line.length), 10);
    // Use different character width multipliers for different text types
    const charWidthMultiplier = textType === 'pre' ? 0.55 : 0.6;
    const newWidth = Math.max(200, maxLineLength * fontSize * charWidthMultiplier);
    const newHeight = Math.max(60, lines.length * lineHeight);
    
    projectStore.updateElement({ 
      id: element.id, 
      updates: { 
        text: newValue,
        width: newWidth,
        height: newHeight
      } 
    });
    
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    uiStore.stopEditingText();
    uiStore.setActiveTool('select');
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    
    if (e.key === 'Escape') {
      setEditValue(text);
      projectStore.updateElement({ id: element.id, updates: { text } });
      uiStore.stopEditingText();
      uiStore.setActiveTool('select');
    } else if (e.key === 'Tab') {
      // Insert 2 spaces instead of tab character
      e.preventDefault();
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editValue.substring(0, start) + '  ' + editValue.substring(end);
      setEditValue(newValue);
      projectStore.updateElement({ id: element.id, updates: { text: newValue } });
      
      // Move cursor after the spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
    // Note: We don't handle Enter here anymore since we want multiline support
  };

  return (
    <>
      <g transform={`rotate(${rotation}, ${x + width/2}, ${y + height/2})`}>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          style={{ cursor: isSelected ? 'text' : 'default' }}
        />
        
        {/* Render multiline text */}
        {textLines.map((line, index) => (
          <text
            key={index}
            x={x + (textAlign === 'center' ? width / 2 : textAlign === 'right' ? width : 0)}
            y={y + fontSize + (index * lineHeight)}
            fill={style.strokeColor === 'transparent' ? '#000000' : style.strokeColor}
            fontSize={fontSize}
            fontFamily={fontFamily}
            textAnchor={textAlign}
            opacity={isEditing ? 0 : style.opacity}
            style={{ 
              cursor: isSelected ? 'text' : 'default',
              filter: textType === 'sloppy' ? 'url(#roughPaper)' : 'none',
              fontWeight: textType === 'pre' ? '400' : 'normal',
              letterSpacing: textType === 'pre' ? '0' : '0.5px',
              pointerEvents: 'none',
              whiteSpace: 'pre'
            }}
          >
            {line}
          </text>
        ))}
        
        {isSelected && !isEditing && (
          <rect
            x={x - 2}
            y={y - 2}
            width={width + 4}
            height={height + 4}
            fill="none"
            stroke="#007acc"
            strokeWidth={2}
            strokeDasharray="5,5"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </g>
      
      {isEditing && (
        <foreignObject
          x={x - 4}
          y={y - 4}
          width={width + 8}
          height={height + 8}
        >
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={handleTextareaChange}
            onBlur={handleTextareaBlur}
            onKeyDown={handleTextareaKeyDown}
            style={{
              width: '100%',
              minHeight: '100%',
              border: 'none',
              padding: '4px',
              fontSize: `${fontSize}px`,
              fontFamily: fontFamily,
              textAlign: textAlign,
              background: 'transparent',
              color: style.strokeColor === 'transparent' ? '#000000' : style.strokeColor,
              outline: '1px solid #007acc',
              boxShadow: 'none',
              borderRadius: '2px',
              resize: 'both',
              lineHeight: `${lineHeight / fontSize}`,
              whiteSpace: textType === 'pre' ? 'pre' : 'pre-wrap',
              fontWeight: textType === 'pre' ? '400' : 'normal',
              letterSpacing: textType === 'pre' ? '0' : '0.5px',
            }}
            placeholder="Type your text here..."
          />
        </foreignObject>
      )}
    </>
  );
}); 